// Bridges React Router <-> the native shell. Renders nothing.
// Design: docs/FeatureDesigns/hybrid-shell-chrome-contract.md §5–6.
//
//  - lets native drive SPA routes via window.__shellNavigate(path)
//  - pushes the current screen's chrome descriptor to native on every route change
//
// No-ops entirely when not running inside the shell, so it is safe to mount
// unconditionally in the web app.

import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isInShell, registerNavigator, setChrome } from './bridge';
import { getChromeForPath } from './chromeRegistry';
import { useAuth } from '../hooks/useAuth';

export function ShellSync() {
  const navigate = useNavigate();
  const location = useLocation();
  const { startGuest } = useAuth();

  // native → web: allow the shell to drive client-side navigation.
  useEffect(() => {
    if (!isInShell()) return;
    return registerNavigator((path) => navigate(path));
  }, [navigate]);

  // Native First-Run "Try a … question": the shell renders the pick UI natively,
  // but the web still owns the guest session + FRE entry. The shell loads
  // `/?shell_fre=1&exam=&subject=&grade=` — the picks ride in the URL (not a
  // window global), so there's no race with hydration. On boot we read them,
  // bootstrap a guest, and enter /first-run; FirstRun consumes the picks → solve.
  useEffect(() => {
    if (!isInShell()) return;
    const params = new URLSearchParams(location.search);
    if (params.get('shell_fre') !== '1') return;
    window.__shellFrePick = {
      exam: params.get('exam') ?? '',
      subject: params.get('subject') ?? '',
      grade: Number(params.get('grade') ?? '0'),
    };
    let cancelled = false;
    (async () => {
      try {
        await startGuest();
      } catch {
        /* if guest bootstrap fails, /first-run bounces to /login */
      }
      if (!cancelled) navigate('/first-run', { replace: true });
    })();
    return () => { cancelled = true; };
  }, [location.search, navigate, startGuest]);

  // web → native: keep native chrome in sync with the current route.
  useEffect(() => {
    if (!isInShell()) return;
    setChrome(getChromeForPath(location.pathname));
  }, [location.pathname]);

  return null;
}
